import React, { useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Check, Copy } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusPill } from '@/components/StatusPill';
import { formatMetaCount, formatMetaInsightsValue, formatMetaPercent } from '@/utils/currency';
import type { DateRangeValue } from '@/components/DateRangePicker';

/*
 * Presentation primitives shared by the five TikTok Measurement tabs
 * (Reporting · Audiences · Pixel · Instant Forms · Event Rules). They exist so
 * every tab has the same body padding, toolbar height, table density and
 * skeleton shapes — the tabs are one page to the user.
 */

// ── layout ──────────────────────────────────────────────────────────────────

/** Body wrapper for a tab: full width, same gutters and vertical rhythm everywhere. */
export const TabBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="w-full space-y-5 px-7 pb-24 pt-5">{children}</div>
);

/**
 * One toolbar row per tab: title + count on the left, actions on the right.
 * Fixed min-height so switching tabs never shifts the content below it.
 */
export const TabToolbar: React.FC<{
  title: string;
  count?: number | null;
  subtitle?: string;
  right?: React.ReactNode;
}> = ({ title, count, subtitle, right }) => (
  <div className="flex min-h-[40px] flex-wrap items-center justify-between gap-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-base font-semibold tracking-tight text-text-primary">{title}</h2>
        {typeof count === 'number' && (
          <span className="rounded-full border border-border bg-surface-alt px-2 py-0.5 text-2xs font-semibold tabular-nums text-text-muted">
            {count.toLocaleString()}
          </span>
        )}
      </div>
      {subtitle && <p className="mt-0.5 text-xs text-text-muted">{subtitle}</p>}
    </div>
    {right && <div className="flex flex-wrap items-center gap-2">{right}</div>}
  </div>
);

/** Small inline note (info / warning) — one line, not a banner wall. */
export const InlineHint: React.FC<{ tone?: 'info' | 'warning'; children: React.ReactNode; className?: string }> = ({
  tone = 'info', children, className,
}) => (
  <p className={clsx(
    'flex items-start gap-1.5 text-xs leading-relaxed',
    tone === 'warning' ? 'text-warning' : 'text-text-muted',
    className,
  )}>
    <span className={clsx('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', tone === 'warning' ? 'bg-warning' : 'bg-info')} />
    <span>{children}</span>
  </p>
);

// ── segmented control ───────────────────────────────────────────────────────

export function Segmented<T extends string>({ value, onChange, options, className }: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  className?: string;
}) {
  return (
    <div className={clsx('inline-flex h-9 items-center gap-0.5 rounded-lg border border-border bg-surface-alt/60 p-0.5', className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={clsx(
            'h-full rounded-md px-3 text-xs font-medium transition-colors',
            value === o.value ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── switch ──────────────────────────────────────────────────────────────────

export const Switch: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
}> = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
    className={clsx(
      'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
      checked ? 'border-primary bg-primary' : 'border-border-strong bg-surface-alt',
      disabled && 'cursor-wait opacity-60',
    )}
  >
    <span className={clsx(
      'inline-block h-3.5 w-3.5 rounded-full bg-surface shadow-soft transition-transform',
      checked ? 'translate-x-[18px]' : 'translate-x-0.5',
    )} />
  </button>
);

// ── KPI tile ────────────────────────────────────────────────────────────────

export const KpiTile: React.FC<{
  label: string;
  value: string;
  sub?: string;
  icon?: React.ReactNode;
  loading?: boolean;
}> = ({ label, value, sub, icon, loading }) => (
  <Card className="flex flex-col justify-between !p-4">
    <div className="flex items-center justify-between gap-2">
      <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{label}</span>
      {icon && <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-soft text-primary">{icon}</span>}
    </div>
    {loading ? (
      <div className="skeleton mt-3 h-7 w-24 rounded-md" />
    ) : (
      <div className="mt-2 truncate font-display text-[22px] font-bold tabular-nums tracking-tight text-text-primary" title={value}>
        {value}
      </div>
    )}
    {sub && <div className="mt-1 text-2xs text-text-muted">{sub}</div>}
  </Card>
);

// ── table ───────────────────────────────────────────────────────────────────

/** Card-wrapped scroll area for a real <table>; header sticks inside it. */
export const TableCard: React.FC<{ children: React.ReactNode; maxHeight?: number | string; minWidth?: number }> = ({
  children, maxHeight = '60vh', minWidth = 720,
}) => (
  <Card padded={false} className="overflow-hidden">
    <div className="overflow-auto" style={{ maxHeight }}>
      <table className="w-full border-separate border-spacing-0 text-sm" style={{ minWidth }}>
        {children}
      </table>
    </div>
  </Card>
);

export const Th: React.FC<{
  children?: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
  onClick?: () => void;
  sortDir?: 'asc' | 'desc' | null;
  width?: string;
}> = ({ children, align = 'left', className, onClick, sortDir, width }) => (
  <th
    onClick={onClick}
    style={width ? { width } : undefined}
    aria-sort={sortDir ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
    className={clsx(
      'sticky top-0 z-10 whitespace-nowrap border-b border-border bg-surface-alt px-4 py-2.5',
      'text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted',
      align === 'right' ? 'text-right' : 'text-left',
      onClick && 'cursor-pointer select-none hover:text-text-primary',
      sortDir && 'text-text-primary',
      className,
    )}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {sortDir && <span aria-hidden className="text-primary">{sortDir === 'asc' ? '↑' : '↓'}</span>}
    </span>
  </th>
);

export const Td: React.FC<{
  children?: React.ReactNode;
  align?: 'left' | 'right';
  mono?: boolean;
  muted?: boolean;
  className?: string;
}> = ({ children, align = 'left', mono, muted, className }) => (
  <td className={clsx(
    'border-b border-border px-4 py-3 align-middle',
    align === 'right' && 'text-right tabular-nums',
    mono && 'font-mono text-xs',
    muted ? 'text-text-muted' : 'text-text-primary',
    className,
  )}>
    {children}
  </td>
);

export const Tr: React.FC<{ children: React.ReactNode; onClick?: () => void; title?: string; className?: string }> = ({
  children, onClick, title, className,
}) => (
  <tr
    onClick={onClick}
    title={title}
    className={clsx(
      'group transition-colors even:bg-surface-alt/30 hover:bg-surface-alt/70 last:[&>td]:border-b-0',
      onClick && 'cursor-pointer',
      className,
    )}
  >
    {children}
  </tr>
);

// ── skeletons ───────────────────────────────────────────────────────────────

export const SkeletonTable: React.FC<{ rows?: number; cols?: number }> = ({ rows = 6, cols = 5 }) => (
  <Card padded={false} className="overflow-hidden">
    <div className="border-b border-border bg-surface-alt px-4 py-3">
      <div className="skeleton h-3 w-40 rounded" />
    </div>
    <div className="divide-y divide-border">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="grid items-center gap-4 px-4 py-3.5" style={{ gridTemplateColumns: `2fr repeat(${cols - 1}, 1fr)` }}>
          {Array.from({ length: cols }).map((__, j) => (
            <div key={j} className={clsx('skeleton h-3.5 rounded', j === 0 ? 'w-3/4' : 'ml-auto w-1/2')} />
          ))}
        </div>
      ))}
    </div>
  </Card>
);

export const SkeletonCards: React.FC<{ count?: number; height?: number }> = ({ count = 3, height = 132 }) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <Card key={i} className="space-y-3" style={{ minHeight: height }}>
        <div className="flex items-center gap-3">
          <div className="skeleton h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <div className="skeleton h-3.5 w-2/3 rounded" />
            <div className="skeleton h-3 w-1/3 rounded" />
          </div>
        </div>
        <div className="skeleton h-3 w-full rounded" />
        <div className="skeleton h-3 w-5/6 rounded" />
      </Card>
    ))}
  </div>
);

export const SkeletonBlock: React.FC<{ height?: number }> = ({ height = 224 }) => (
  <div className="skeleton w-full rounded-xl" style={{ height }} />
);

// ── monospace copy card ─────────────────────────────────────────────────────

export const MonoCopyCard: React.FC<{ title: string; code: string; hint?: React.ReactNode; rows?: number }> = ({
  title, code, hint, rows = 8,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Copied to clipboard');
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Copy failed — select the text and copy manually');
    }
  };
  return (
    <div className="overflow-hidden rounded-xl border border-border">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-surface-alt px-3 py-2">
        <span className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{title}</span>
        <button
          type="button"
          onClick={copy}
          className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-xs font-medium text-text-secondary hover:border-border-strong hover:text-text-primary"
        >
          {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <textarea
        readOnly
        value={code}
        rows={rows}
        spellCheck={false}
        onFocus={(e) => e.currentTarget.select()}
        className="block w-full resize-none bg-surface px-3 py-2.5 font-mono text-[11.5px] leading-relaxed text-text-primary focus:outline-none"
      />
      {hint && <div className="border-t border-border bg-surface px-3 py-2">{hint}</div>}
    </div>
  );
};

// ── pills ───────────────────────────────────────────────────────────────────

type PillVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export function audienceStatusVariant(status?: string): PillVariant {
  const s = (status ?? '').toUpperCase();
  if (!s) return 'neutral';
  if (/READY|VALID|AVAILABLE|ACTIVE|NORMAL/.test(s) && !/INVALID|UNAVAILABLE|INACTIVE/.test(s)) return 'success';
  if (/CALCULAT|PROCESS|PENDING|EXPIRING|UPLOAD|BUILD/.test(s)) return 'warning';
  if (/INVALID|EXPIRED|FAIL|ERROR|UNAVAILABLE|DELETED/.test(s)) return 'danger';
  return 'neutral';
}

export function isLookalikeType(type?: string): boolean {
  return /LOOKALIKE/i.test(type ?? '');
}

export const AudienceTypePill: React.FC<{ type?: string }> = ({ type }) => (
  <StatusPill
    status={type ?? 'custom'}
    label={isLookalikeType(type) ? 'Lookalike' : 'Custom'}
    variant={isLookalikeType(type) ? 'info' : 'neutral'}
  />
);

export function formStatusVariant(status?: string): PillVariant {
  const s = (status ?? '').toUpperCase();
  if (/PUBLISH|ACTIVE|ENABLE|LIVE/.test(s)) return 'success';
  if (/DRAFT|PENDING|REVIEW/.test(s)) return 'warning';
  if (/REJECT|DISABLE|DELETE|ARCHIV/.test(s)) return 'danger';
  return 'neutral';
}

export function titleCase(s?: string): string {
  if (!s) return '—';
  return s.toLowerCase().replace(/_/g, ' ').replace(/(^|\s)\S/g, (m) => m.toUpperCase());
}

// ── formatters (currency-aware, via the shared Meta helpers) ────────────────

export const fmtMoney = (n: number, currency?: string | null) =>
  formatMetaInsightsValue(Number.isFinite(n) ? String(n) : null, currency ?? '');
export const fmtCount = (n: number) => formatMetaCount(Number.isFinite(n) ? String(n) : null);
export const fmtPct = (n: number) => formatMetaPercent(Number.isFinite(n) ? String(n) : null);

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ── date range → API bounds ─────────────────────────────────────────────────

const DAY = 86_400_000;

/** Date-only report rows use UTC midnight, regardless of the browser timezone. */
export function resolveRange(v: DateRangeValue): { from: string; to: string; days: number } {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  let from = new Date(today);
  let to = new Date(today.getTime() + DAY - 1);
  const lastDays = (n: number) => new Date(today.getTime() - (n - 1) * DAY);
  switch (v.preset) {
    case 'today': break;
    case 'yesterday': from = new Date(today.getTime() - DAY); to = new Date(today.getTime() - 1); break;
    case 'last_14d': from = lastDays(14); break;
    case 'last_30d': from = lastDays(30); break;
    case 'last_90d': from = lastDays(90); break;
    case 'this_month': from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)); break;
    case 'last_month': from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)); to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1) - 1); break;
    case 'lifetime': from = lastDays(365); break;
    case 'custom': {
      const start = v.since ? new Date(`${v.since}T00:00:00Z`) : null;
      const end = v.until ? new Date(`${v.until}T23:59:59.999Z`) : null;
      if (start && end && Number.isFinite(start.getTime()) && Number.isFinite(end.getTime())) { from = start; to = end; }
      else from = lastDays(7);
      break;
    }
    default: from = lastDays(7);
  }
  return { from: from.toISOString(), to: to.toISOString(), days: Math.max(1, Math.ceil((to.getTime() - from.getTime()) / DAY)) };
}
